import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSettings } from 'services/settings/SettingsContext';
import TextInput from '../basefields/TextInput';
import TextareaInput from '../basefields/TextareaInput';
import Checkbox from '../basefields/Checkbox';
import Dropdown from '../basefields/Dropdown';
import DateInput from '../basefields/DateInput';
import SwitchButton from 'components/buttons/SwitchButton';
import TabSelect from '../basefields/TabSelect';
import TimeInput from '../basefields/TimeInput';
import { convert24HourTo12HourFormat, formatDate } from 'services/utils/dateTimeUtils';
import { formatPriceCurrencyString } from 'services/utils/amountFormatting';
import { baseUrl } from 'App'
import CustomFieldPhoto from './CustomFieldPhoto';

interface CustomFieldRendererProps {
    field: any;
    value: any;
    shouldAutoFocus?: boolean;
    editable?: boolean;
    objectType?: string;
    itemId?: string | number;
    onChange?: (value: any) => void;
}

const CustomFieldRenderer: React.FC<CustomFieldRendererProps> = ({
    field,
    value,
    shouldAutoFocus = false,
    editable,
    objectType,
    itemId,
    onChange,
}) => {
    const { t } = useTranslation();
    const { userLocale, getTimeFormat } = useSettings();

    // Generate the view mode value formatting
    if (!editable) {
        switch (field.field_type) {
            case 'boolean':
                if (value === true) return t('forms.true_value');
                if (value === false) return t('forms.false_value');
                return '-';

            case 'date':
                return value ? formatDate(value, userLocale) : '-';

            case 'time':
                return value
                    ? getTimeFormat() === '12h'
                        ? convert24HourTo12HourFormat(String(value).slice(0, 5))
                        : String(value).slice(0, 5)
                    : '-';

            case 'number':
                return value !== null && value !== undefined && value !== '' ? new Intl.NumberFormat(userLocale).format(Number(value)) : '-';

            case 'amount':
                return value ? formatPriceCurrencyString(String(value).replace(',', '.'), userLocale, 'EUR') : '-';

            case 'checkbox':
                return Array.isArray(value)
                    ? value
                        .map((checkboxValue: string) => (
                            (field.options || []).find(
                                (option: any) => option.value === checkboxValue
                            )?.label
                        ))
                        .filter(Boolean)
                        .join(', ')
                    : '-';

            case 'dropdown':
            case 'tabs': {
                const selectedOption = (field.options || []).find((option: any) => option.value === value);
                if (!selectedOption) {
                    return '-';
                }

                // Show a colored option as a label badge
                return selectedOption.color
                    ? <span className={`label-badge ${selectedOption.color}`}>{selectedOption.label}</span>
                    : selectedOption.label;
            }

            case 'yes_no':
            case 'acceptable':
            case 'compliant':
                return value ? t(`custom_field.option_presets.${value}`) : '-';

            case 'photo':
                return (
                    <CustomFieldPhoto
                        photos={Array.isArray(value) ? value : []}
                        editable={false}
                        objectType={objectType}
                        itemId={itemId}
                        fieldId={field.id ?? field.field_id}
                        onChange={onChange}
                    />
                )

            case 'signature':
                return (
                    <div className='custom-field-signature-preview'>
                        {value?.file ? (
                            <div className='custom-field-signature-image-wrapper'>
                                <img
                                    src={process.env.NODE_ENV === 'production' ? value.file : `${baseUrl}${value.file}`}
                                    alt={value.filename || 'Signature'}
                                    className='custom-field-signature-image'
                                />
                            </div>
                        ) : (
                            <span>-</span>
                        )}
                    </div>
                );

            default:
                return value || '-';
        }
    }

    // Generate the field for the edit mode
    switch (field.field_type) {
        case 'textarea':
            return (
                <TextareaInput
                    name={`field_${field.id}`}
                    rows={5}
                    value={value || ''}
                    placeholder={field.placeholder || ''}
                    onChange={(value) => {
                        if (onChange) {
                            onChange(
                                typeof value === 'string'
                                    ? value
                                    : value.target.value
                            );
                        }
                    }}
                    autoFocus={shouldAutoFocus}
                />
            );

        case 'dropdown':
            return (
                <Dropdown<any>
                    name={`field_${field.id}`}
                    options={field.options || []}
                    selectedOption={
                        (field.options || []).find(
                            (option: any) => option.value === value
                        ) ?? null
                    }
                    optionFormat='label'
                    optionMode='dot-text'
                    backendField='value'
                    selectionFormat={(option) => option.label}
                    allowNoneOption={false}
                    showSearch={false}
                    onChange={(selectedValue) => {
                        if (onChange) {
                            onChange(selectedValue || null);
                        }
                    }}
                />
            );

        case 'checkbox':
            return (
                <div>
                    {(field.options || []).map((option: any) => {

                        const currentValues = Array.isArray(value)
                            ? value
                            : [];

                        const isChecked = currentValues.includes(option.value);

                        return (
                            <Checkbox
                                key={option.value}
                                name={option.value}
                                label={option.label}
                                align='left'
                                value={isChecked}
                                onChange={() => {

                                    if (!onChange) {
                                        return;
                                    }

                                    if (isChecked) {
                                        onChange(
                                            currentValues.filter(
                                                (checkboxValue: string) =>
                                                    checkboxValue !== option.value
                                            )
                                        );
                                    } else {
                                        onChange([
                                            ...currentValues,
                                            option.value,
                                        ]);
                                    }
                                }}
                            />
                        );
                    })}
                </div>
            );

        case 'boolean':
            return (
                <SwitchButton
                    value={value || false}
                    onToggle={(isOn) => {
                        if (onChange) {
                            onChange(isOn);
                        }
                    }}
                />
            );

        case 'tabs':
        case 'yes_no':
        case 'acceptable':
        case 'compliant':
            return (
                <TabSelect<any>
                    options={field.options || []}
                    selectedOption={
                        (field.options || []).find(
                            (option: any) => option.value === value
                        ) ?? null
                    }
                    optionFormat='label'
                    backendField='value'
                    onChange={(selectedValue) => {
                        if (onChange) {

                            console.log("selectedValue", selectedValue)

                            onChange(selectedValue || null);
                        }
                    }}
                />
            );

        case 'number':
            return (
                <TextInput
                    name={`field_${field.id}`}
                    value={value ?? ''}
                    placeholder='0'
                    onChange={(value) => {
                        if (onChange) {
                            onChange(value);
                        }
                    }}
                    shouldAutoFocus={shouldAutoFocus}
                />
            );

        case 'amount':
            return (
                <div className='preview-price-field'>
                    <span className='currency-prefix'>
                        €
                    </span>
                    <TextInput
                        name={`field_${field.id}`}
                        value={value ?? ''}
                        placeholder='0,00'
                        onChange={(value) => {
                            if (onChange) {
                                const cleanedValue = value.replace(/[^0-9.,-]/g, '');
                                onChange(cleanedValue);
                            }
                        }}
                        shouldAutoFocus={shouldAutoFocus}
                    />
                </div>
            );

        case 'date':
            return (
                <DateInput
                    name={`field_${field.id}`}
                    value={value ? new Date(value) : null}
                    onChange={(value) => {
                        if (onChange) {
                            onChange(value);
                        }
                    }}
                />
            );

        case 'time':
            return (
                <TimeInput
                    type='time'
                    name={`field_${field.id}`}
                    value={value || null}
                    onChange={(value) => {
                        if (onChange) {
                            onChange(value);
                        }
                    }}
                />
            );

        case 'photo':
            return (
                <CustomFieldPhoto
                    photos={Array.isArray(value) ? value : []}
                    editable
                    objectType={objectType}
                    itemId={itemId}
                    fieldId={field.id ?? field.field_id}
                    onChange={onChange}
                />
            );
        
        case 'signature':
            return (
                <div className='custom-field-signature-preview'>
                    {value?.file ? (
                        <div className='custom-field-signature-image-wrapper'>
                            <img
                                src={process.env.NODE_ENV === 'production' ? value.file : `${baseUrl}${value.file}`}
                                alt={value.filename || 'Signature'}
                                className='custom-field-signature-image'
                            />
                        </div>
                    ) : (
                        <span>-</span>
                    )}
                </div>
            );

        default:
            return (
                <TextInput
                    name={`field_${field.id}`}
                    value={value || ''}
                    placeholder={field.placeholder || ''}
                    onChange={(value) => {
                        if (onChange) {
                            onChange(value);
                        }
                    }}
                    shouldAutoFocus={shouldAutoFocus}
                />
            );
    }
};

export default CustomFieldRenderer;